import {config} from 'config/env';
import ReactNative from 'services/react-native';
import {CurrentStoreType} from 'store/types/app';
import {BasketLineItem, MarketplaceShopType, MetaDataResponse, UserAddress} from 'types';

export const getValue = (obj: any, path: any, defaultValue?: any) => {
  if (!obj || (typeof path !== 'string' && !Array.isArray(path))) {
    return defaultValue;
  }

  const pathArray = Array.isArray(path) ? path : path.split('.').filter(Boolean);

  let result = obj;
  for (const key of pathArray) {
    result = result == null ? undefined : result[key];
    if (result === undefined) {
      return defaultValue;
    }
  }
  return result;
};

export const omit = <T extends Record<string, any>>(obj: any, keys: any): Partial<T> => {
  if (!obj) return {};

  return Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key as keyof T))) as Partial<T>;
};

export const capitalizeFirstLetter = (text: string) => {
  return text?.charAt(0).toUpperCase() + text?.slice(1) || '';
};

type DebouncedFunction<T extends (...args: any[]) => any> = (...args: Parameters<T>) => void;

export const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): DebouncedFunction<T> => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const safeToFixed = (value: any, decimals = 2, defaultValue = '0.00') => {
  try {
    // Check if value is a valid number
    if (typeof value === 'number' && !isNaN(value)) {
      return value.toFixed(decimals);
    }

    // Try to convert to number if value is a string
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return numValue.toFixed(decimals);
    }

    // If parsing or toFixed fails, return default
    return defaultValue;
  } catch (error) {
    // Catch any other unexpected errors and return default
    return defaultValue;
  }
};

export function getTokenFromCookies(cookieName: string): string | null {
  try {
    // Validate input
    if (!cookieName || typeof cookieName !== 'string') {
      throw new Error('Invalid cookie name provided.');
    }

    // Check if cookies are accessible (basic feature detection for older environments)
    if (typeof document === 'undefined' || typeof document.cookie === 'undefined') {
      throw new Error('Cookies are not accessible in this environment.');
    }

    // Match the cookie name using a regular expression
    const cookiePattern = new RegExp(`(^|;\\s*)${cookieName}=([^;]*)`);
    const match = document.cookie.match(cookiePattern);

    // Return the token if found, otherwise null
    return match ? decodeURIComponent(match[2]) : null;
  } catch (error) {
    console.error('Error getting token from cookies:', error);
    return null; // Fail gracefully
  }
}

export const isSyncBasketEnabled = () => {
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isSyncBasket =
    (!isReactNativeView && config.IS_WEB_SYNC_BASKET_ENABLE) || (isReactNativeView && config.IS_APP_SYNC_BASKET_ENABLE);
  return isSyncBasket;
};

export const fetchShippingAddress = (availableAddresses: UserAddress[], currentStore?: CurrentStoreType) => {
  let relevantAddress = availableAddresses.find(
    (address) =>
      address.zip === currentStore?.zipcode &&
      (address.subDistrict?.nameEn === currentStore?.subdistrict ||
        address.subDistrict?.nameTh === currentStore?.subdistrict) &&
      address.isDefault === true
  );
  if (!relevantAddress) {
    relevantAddress = availableAddresses.find(
      (address) =>
        address.zip === currentStore?.zipcode &&
        (address.subDistrict?.nameEn === currentStore?.subdistrict ||
          address.subDistrict?.nameTh === currentStore?.subdistrict)
    );
  }
  return relevantAddress;
};

export const isSyncAddressEnabled = () => {
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isSyncAddress =
    (!isReactNativeView && config.IS_WEB_SYNC_ADDRESS_ENABLE) ||
    (isReactNativeView && config.IS_APP_SYNC_ADDRESS_ENABLE);
  return isSyncAddress;
};

export function filter1PItems(basketItems: BasketLineItem[]) {
  const onePItems = basketItems?.filter(
    (item: any) =>
      (item.productDetails?.sellerDetails?.marketplaceType === null ||
        item.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.CONSIGNMENT) &&
      item.productDetails?.sellerDetails.fulfilledByPlatform === 'Maknet'
  );
  return onePItems;
}

export function filter3PItems(basketItems: BasketLineItem[]) {
  const threePItems = basketItems?.filter(
    (item: any) =>
      (item.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.DROPSHIP_FBM ||
        item.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.DROPSHIP_SOD) &&
      item.productDetails?.sellerDetails.fulfilledByPlatform === null
  );
  return threePItems;
}

export function extractStoreInfoData(metaData: MetaDataResponse) {
  const selectedStore = metaData?.storeInfo[0];
  const isSameDayDelivery = selectedStore?.sameDay;
  const showStorePickup = selectedStore?.warehouseStoreData?.storeConfigurations?.pickupAtStoreEnable;
  const storeCodes = metaData.storeInfo?.map((store) => store.storeCode) ?? [];

  return {selectedStore, isSameDayDelivery, showStorePickup, storeCodes};
}
